/**
 * AdOverlay - the "Paused. Continue?" monetisation gate.
 *
 *  - mode "interstitial": shows a native interstitial, then closes.
 *  - mode "appopen": triggers an OS-level request (default browser OR
 *    notification permission, alternating). The native side calls back into a
 *    one-shot global callback; we then close shortly after, with a safety
 *    timeout in case the callback never fires.
 *
 * A heartbeat keeps the native ads-overlay cooldown timer warm while visible.
 */
import { useRef, useEffect } from 'preact/hooks';
import {
  adsOverlayCooldown,
  showInterstitial,
  requestSetDefaultBrowser,
  requestNotificationPermission,
  getInterface,
} from '../bridge/puerInterface.js';
import {
  nextAppopenTrigger,
  rememberAppopenTrigger,
} from '../lib/adScheduler.js';

const CONTINUE_DELAY_MS = 3_000;
const SAFETY_TIMEOUT_MS = 15_000;

export function AdOverlay({ mode, onClose }) {
  const continueTimer = useRef(0);
  const safetyTimer = useRef(0);
  const callbackName = useRef('');

  useEffect(() => {
    const beat = () => adsOverlayCooldown();
    beat();
    const interval = setInterval(beat, 3_000);
    return () => {
      clearInterval(interval);
      if (continueTimer.current) {
        clearTimeout(continueTimer.current);
        continueTimer.current = 0;
      }
      if (safetyTimer.current) {
        clearTimeout(safetyTimer.current);
        safetyTimer.current = 0;
      }
      if (callbackName.current && window[callbackName.current]) {
        cleanupCallback();
      }
    };
  }, []);

  function cleanupCallback() {
    const name = callbackName.current;
    if (!name) return;
    try {
      delete window[name];
    } catch {
      window[name] = undefined;
    }
    callbackName.current = '';
  }

  function handleContinue() {
    if (mode === 'appopen') {
      const trigger = nextAppopenTrigger();
      rememberAppopenTrigger(trigger);

      const name = `__adsTriggerCb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      callbackName.current = name;

      // One-shot callback the native shell invokes once the OS dialog resolves.
      window[name] = () => {
        if (callbackName.current === name) cleanupCallback();
        if (safetyTimer.current) {
          clearTimeout(safetyTimer.current);
          safetyTimer.current = 0;
        }
        if (continueTimer.current) clearTimeout(continueTimer.current);
        continueTimer.current = window.setTimeout(() => {
          continueTimer.current = 0;
          onClose();
        }, CONTINUE_DELAY_MS);
      };

      // Safety net: never trap the user if the callback never arrives.
      safetyTimer.current = window.setTimeout(() => {
        safetyTimer.current = 0;
        if (callbackName.current === name) cleanupCallback();
        onClose();
      }, SAFETY_TIMEOUT_MS);

      const expr = `window.${name}`;
      if (trigger === 'browser') {
        requestSetDefaultBrowser(expr);
      } else {
        requestNotificationPermission(expr);
      }
      return;
    }

    // Interstitial path.
    const iface = getInterface();
    if (iface && typeof iface.showInterstitial === 'function') {
      showInterstitial();
      return;
    }
    onClose();
  }

  return (
    <div class="ad">
      <div class="ad__dialog">
        <span class="ad__title">Paused. Do you want to continue?</span>
        <button type="button" class="ad__continue" onClick={handleContinue}>
          Continue to video
        </button>
      </div>
    </div>
  );
}
